import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import { KycStatus } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { useConsumeQueryParam } from 'common/bento/lib/useConsumeQueryParam';
import { apolloClient } from 'common/graphql/clients';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';
import IdentitySuccess from 'features/Onboarding/pages/IdentitySuccess';

import { GetKycIdStatusDocument } from '../Identity/graphql/queries/getKycIdStatus.gql';
import { QesModuleDataDocument } from './graphql/queries/qesModuleData.gql';
import machineConfig, { model, State } from './machine';
import { Model } from './machine/model';
import FourthlineSignature from './pages/FourthlineSignature/FourthlineSignature';
import Ineligible from './pages/Ineligible';

interface QesProps {
  companyProfileId: string;
}

const QesComponent = withStateMachine<Model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;
    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.SIGNATURE: {
        if (!state.context.signatureContext) {
          throw new Error('Missing signature context');
        }

        const handleErrorOrRestart = async () => {
          // For consistency reasons and the fact that the error `flOnboardingStatus` event isn't triggered reliably,
          // we re-refetch the kycIdStatus here from network :pls:
          const { data } = await apolloClient.query({
            fetchPolicy: 'network-only',
            query: GetKycIdStatusDocument,
            variables: { companyProfileId: moduleData.companyProfileId },
          });

          // This is a specific case where the ID Check has been refused between the two flows.
          // Reloading the page entirely will send you back on the Verification Home, with an invalid ID status.
          if (data?.viewer.companyUser.kycIdStatus === KycStatus.Refused) {
            return send(model.events.INVALID_IDENTITY_PROVIDER_ERROR());
          }

          return send(model.events.RESTART_SIGNATURE());
        };

        return (
          <FourthlineSignature
            companyProfileId={moduleData.companyProfileId}
            fourthlineSignatureToken={state.context.signatureContext.token}
            onFlowError={handleErrorOrRestart}
            onPrev={onPrev}
            onRestartSignature={handleErrorOrRestart}
            onSignatureCompleted={() => send(model.events.NEXT())}
          />
        );
      }

      case State.SUCCESS:
        return (
          <IdentitySuccess
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      case State.INELIGIBLE:
        return <Ineligible onPrev={onPrev} />;

      case State.INIT:
      case State.START_SIGNATURE:
      case State.FOURTHLINE_NATIVE_WORKFLOW:
      case State.FINISH_SIGNATURE:
      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },

  {
    devTools: true,
    machineConfig,
  },
);

const Qes = asBentoModule<QesProps>(({ companyProfileId, onDone }) => {
  const query = useQuery(QesModuleDataDocument, {
    variables: { companyProfileId },
  });

  const bootstrap = useConsumeQueryParam(`bootstrap:${machineConfig.id}`);

  return (
    <SuspenseQuery loader={<OBLoaderPage onPrev={() => null} />} query={query}>
      {(queryResult) => (
        <QesComponent
          moduleData={{
            bootstrap,
            companyProfileId,
            ...queryResult,
          }}
          onDone={onDone}
        />
      )}
    </SuspenseQuery>
  );
});

export default Qes;
